/**
 * AdminSessionContext - Session management for admin users
 * Features:
 * - Multi-tab sync via digitalvault-session-admin
 * - Inactivity timeout: 15 minutes (stricter for security)
 * - Activity tracking
 * - Automatic logout on session expiry
 * - 401 interceptor
 */

import React, { createContext, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import InactivityWarning from '../components/InactivityWarning';
import logger from '../utils/logger';
import apiClient from '../utils/apiConfig';
import {
  initTabSync,
  stopTabSync,
  broadcastLogout,
  broadcastSessionExtended
} from '../utils/tabSync';
import { handleRemoteActivity } from '../utils/activityTracker';
import { getRoleChannelName, getRoleInactivityTimeout, SESSION_TYPES } from '../utils/sessionManager';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNEL_NAME = getRoleChannelName(SESSION_TYPES.ADMIN);
const INACTIVITY_TIMEOUT_MINUTES = getRoleInactivityTimeout(SESSION_TYPES.ADMIN);

const INACTIVITY_TIMEOUT_ENABLED = import.meta.env.VITE_ENABLE_INACTIVITY_TIMEOUT !== 'false';
const TAB_SYNC_ENABLED = import.meta.env.VITE_ENABLE_TAB_SYNC !== 'false';

const AdminSessionContext = createContext(null);

export const AdminSessionProvider = ({ children, adminData, onLogout }) => {
  const handleInactivityLogout = useCallback(async () => {
    logger.log('Admin inactive - triggering auto logout');

    // Broadcast logout to other admin tabs FIRST
    if (TAB_SYNC_ENABLED) {
      broadcastLogout('inactivity', CHANNEL_NAME);
    }

    toast.warning('You have been logged out due to inactivity.', {
      position: 'top-center',
      autoClose: 5000
    });

    try {
      const token = localStorage.getItem('adminToken');
      if (token) {
        await axios.post(`${API}/admin/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      logger.error('Admin logout API call failed:', error);
    }

    if (onLogout) {
      onLogout();
    }

    window.location.href = '/admin';
  }, [onLogout]);

  const {
    showWarning,
    warningSeconds,
    handleStayLoggedIn,
    handleAutoLogout
  } = useInactivityTimeout(
    handleInactivityLogout,
    INACTIVITY_TIMEOUT_MINUTES,
    CHANNEL_NAME,
    INACTIVITY_TIMEOUT_ENABLED && !!adminData
  );

  // Setup 401 interceptor for both axios and apiClient
  useEffect(() => {
    const handle401Error = (error) => {
      if (error.response?.status === 401) {
        const requestUrl = error.config?.url || '';

        // Skip interceptor for admin login endpoint
        // The login page should handle its own error messages
        const isAdminLoginEndpoint = requestUrl.includes('/admin') &&
                                     !requestUrl.includes('/admin/');

        if (isAdminLoginEndpoint) {
          // Let the login page handle the error
          return Promise.reject(error);
        }

        logger.log('Admin session expired (401) - forcing logout');
        toast.error('Your admin session has expired. Please login again.', {
          position: 'top-center',
          autoClose: 5000
        });

        if (TAB_SYNC_ENABLED) {
          broadcastLogout('session_expired', CHANNEL_NAME);
        }

        if (onLogout) {
          onLogout();
        }

        window.location.href = '/admin';
      }
      return Promise.reject(error);
    };

    // Interceptor for global axios instance
    const axiosInterceptor = axios.interceptors.response.use(
      response => response,
      handle401Error
    );

    // Interceptor for apiClient instance
    const apiClientInterceptor = apiClient.interceptors.response.use(
      response => response,
      handle401Error
    );

    return () => {
      axios.interceptors.response.eject(axiosInterceptor);
      apiClient.interceptors.response.eject(apiClientInterceptor);
    };
  }, [onLogout]);

  // Setup tab sync
  useEffect(() => {
    if (!TAB_SYNC_ENABLED || !adminData) return;

    const tabSyncCallbacks = {
      // When another tab is active, sync the activity
      onActivity: (data) => {
        logger.log('[AdminTabSync] Activity detected in another tab, syncing...');
        handleRemoteActivity(data);
      },

      // When another tab shows warning, just log it
      onWarning: (data) => {
        logger.log('[AdminTabSync] Warning shown in another tab');
      },

      // When another tab logs out, logout this tab too
      onLogout: (data) => {
        logger.log('[AdminTabSync] Logout detected in another tab, logging out...');
        toast.info('You have been logged out from another admin tab.', {
          position: 'top-center',
          autoClose: 3000
        });

        if (onLogout) {
          onLogout();
        }

        // Small delay to show toast
        setTimeout(() => {
          window.location.href = '/admin';
        }, 500);
      },

      // When another tab extends session, sync here
      onSessionExtended: () => {
        logger.log('[AdminTabSync] Session extended in another tab, syncing...');
        handleRemoteActivity({ timestamp: Date.now() });
        toast.success('Admin session extended in another tab!', {
          position: 'top-center',
          autoClose: 2000
        });
      }
    };

    const syncInitialized = initTabSync(tabSyncCallbacks, CHANNEL_NAME);
    if (syncInitialized) {
      logger.log('[AdminSessionProvider] Multi-tab sync enabled for admin');
    }

    return () => {
      stopTabSync(CHANNEL_NAME);
    };
  }, [adminData, onLogout]);

  const handleStayLoggedInWithToast = useCallback(() => {
    handleStayLoggedIn();

    // Broadcast to other tabs
    if (TAB_SYNC_ENABLED) {
      broadcastSessionExtended(CHANNEL_NAME);
    }

    toast.success('Admin session extended!', {
      position: 'top-center',
      autoClose: 2000
    });
  }, [handleStayLoggedIn]);

  return (
    <AdminSessionContext.Provider value={{
      inactivityTimeout: INACTIVITY_TIMEOUT_MINUTES,
      channelName: CHANNEL_NAME,
      inactivityEnabled: INACTIVITY_TIMEOUT_ENABLED,
      tabSyncEnabled: TAB_SYNC_ENABLED
    }}>
      {children}

      {INACTIVITY_TIMEOUT_ENABLED && (
        <InactivityWarning
          show={showWarning}
          secondsRemaining={warningSeconds}
          onStayLoggedIn={handleStayLoggedInWithToast}
          onLogout={handleAutoLogout}
          roleType="Admin"
        />
      )}
    </AdminSessionContext.Provider>
  );
};


export const useAdminSession = () => {
  const context = useContext(AdminSessionContext);
  if (!context) {
    throw new Error('useAdminSession must be used within AdminSessionProvider');
  }
  return context;
};
